/**
 * Catalogos del formulario de requisicion (B1).
 *
 * Viven aqui y no dentro de cada pantalla porque las mismas listas se pintan en
 * tres lugares —alta, detalle/edicion y las etiquetas de consulta— y estaban
 * copiadas en los tres. Con una opcion nueva en una sola, editar una
 * requisicion la cambiaba en silencio: el `select` no tenia ese valor, mostraba
 * el primero de la lista y al guardar lo escribia.
 *
 * Los `value` son exactamente los del ENUM de MySQL. Agregar uno aqui sin su
 * migracion hace que la requisicion truene al guardar.
 */
export interface OpcionDeCatalogo {
  value: string;
  label: string;
}

export const TIPOS_DE_CONTRATO: OpcionDeCatalogo[] = [
  { value: 'tiempo_completo', label: 'Tiempo completo' },
  { value: 'medio_tiempo', label: 'Medio tiempo' },
  { value: 'practicas', label: 'Prácticas profesionales' },
  { value: 'honorarios', label: 'Honorarios' },
  // Sale siempre, sea cual sea el perfil. Antes se ofrecía aparte y solo al
  // técnico, y el operativo y el administrativo no lo veían.
  { value: 'por_proyecto', label: 'Por proyecto' },
];

/**
 * Escolaridad, de menor a mayor.
 *
 * El catalogo arrancaba en secundaria y las vacantes operativas no cabian.
 * Las truncas estan porque se piden asi en campo —pasante, prepa sin
 * certificado— y no son un nivel a medias sino un requisito real.
 */
export const NIVELES_DE_ESCOLARIDAD: OpcionDeCatalogo[] = [
  { value: 'primaria', label: 'Primaria' },
  { value: 'secundaria', label: 'Secundaria' },
  { value: 'preparatoria_trunca', label: 'Preparatoria trunca' },
  { value: 'preparatoria', label: 'Preparatoria / Bachillerato' },
  { value: 'nivel_tecnico', label: 'Carrera técnica' },
  { value: 'licenciatura_trunca', label: 'Licenciatura trunca (pasante)' },
  { value: 'licenciatura', label: 'Licenciatura' },
  { value: 'maestria', label: 'Maestría' },
  { value: 'doctorado', label: 'Doctorado' },
];

export const MOTIVOS_DE_SOLICITUD: OpcionDeCatalogo[] = [
  { value: 'nueva', label: 'Nueva posición' },
  { value: 'reemplazo', label: 'Reemplazo' },
  { value: 'temporal', label: 'Temporal' },
  { value: 'incapacidad', label: 'Incapacidad' },
];

/** Diccionario value → label, para pintar un valor guardado. */
export const comoDiccionario = (
  opciones: OpcionDeCatalogo[],
): Record<string, string> =>
  Object.fromEntries(opciones.map((o) => [o.value, o.label]));

/** Nivel de ingles que pide la vacante (RH-02). */
export type NivelDeIngles = 'bilingual' | 'intermediate' | 'technical_basic'

export const NIVELES_DE_INGLES: { value: NivelDeIngles; label: string }[] = [
  { value: 'bilingual', label: 'Bilingüe' },
  { value: 'intermediate', label: 'Intermedio' },
  { value: 'technical_basic', label: 'Técnico-Básico' },
]

/** Nivel exigido de un programa o sistema (RH-02). */
export type NivelDeSoftware = 'expert' | 'advanced' | 'intermediate' | 'basic'

export const NIVELES_DE_SOFTWARE: { value: NivelDeSoftware; label: string }[] = [
  { value: 'expert', label: 'Experto' },
  { value: 'advanced', label: 'Avanzado' },
  { value: 'intermediate', label: 'Intermedio' },
  { value: 'basic', label: 'Básico' },
]

export interface SoftwareRequerido {
  name: string
  level: NivelDeSoftware
}

/** El tope lo valida tambien el backend; aqui es para no dejar agregar de mas. */
export const MAXIMO_DE_SOFTWARE = 3

export const etiquetaDeIngles = (valor?: NivelDeIngles | null) =>
  NIVELES_DE_INGLES.find((n) => n.value === valor)?.label ?? 'No especificado'

export const etiquetaDeSoftware = (valor?: NivelDeSoftware | null) =>
  NIVELES_DE_SOFTWARE.find((n) => n.value === valor)?.label ?? '—'
